import { useState } from "react";
import { X } from "lucide-react";
import { Button } from "../../primitives/Button";
import { Select } from "../../primitives/Select";
import { Input } from "../../primitives/Input";
import { simulatePromotion } from "../../../api/catalog";
import type { PriceBook } from "../../../api/catalog";
import type { Product } from "../../../api/catalog";

type SimulationPanelProps = {
  onClose: () => void;
  priceBooks: PriceBook[];
  products: Product[];
};

export function SimulationPanel({ onClose, priceBooks, products }: SimulationPanelProps) {
  const [productId, setProductId] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [area, setArea] = useState("");
  const [priceBookId, setPriceBookId] = useState("");
  const [code, setCode] = useState("");
  const [result, setResult] = useState<{
    base_price: number;
    promotions_applied: Array<{ name: string; discount_amount: number }>;
    discount_amount: number;
    final_price: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSimulate = async () => {
    if (!productId) {
      setError("Sélectionnez un produit");
      return;
    }
    setError(null);
    setResult(null);
    setLoading(true);
    try {
      const res = await simulatePromotion({
        product_id: productId,
        price_book_id: priceBookId || undefined,
        quantity: parseFloat(quantity) || 1,
        area: area ? parseFloat(area) : undefined,
        code: code || undefined,
      });
      setResult({
        base_price: res.base_price,
        promotions_applied: res.promotions_applied.map((p) => ({
          name: p.name,
          discount_amount: p.discount_amount,
        })),
        discount_amount: res.discount_amount,
        final_price: res.final_price,
      });
    } catch (e) {
      setError((e as Error).message || "Erreur de simulation");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-y-0 right-0 w-full max-w-md bg-surface border-l border-border shadow-xl z-50 flex flex-col">
      <div className="flex items-center justify-between p-4 border-b border-border">
        <h2 className="text-lg font-semibold text-text">Simuler une promotion</h2>
        <button
          type="button"
          onClick={onClose}
          className="p-2 rounded hover:bg-surface-muted text-muted"
          aria-label="Fermer"
        >
          <X size={20} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <Select
          label="Produit"
          options={["Sélectionner...", ...products.map((p) => p.name)]}
          value={products.find((p) => p.id === productId)?.name ?? "Sélectionner..."}
          onChange={(label) => {
            const p = products.find((x) => x.name === label);
            setProductId(p?.id ?? "");
          }}
        />
        <Input
          label="Quantité"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
        />
        <Input
          label="Superficie (optionnel)"
          value={area}
          onChange={(e) => setArea(e.target.value)}
        />
        <Select
          label="PriceBook"
          options={["Auto", ...priceBooks.map((pb) => pb.name)]}
          value={priceBooks.find((pb) => pb.id === priceBookId)?.name ?? "Auto"}
          onChange={(label) => {
            const pb = priceBooks.find((x) => x.name === label);
            setPriceBookId(pb?.id ?? "");
          }}
        />
        <Input
          label="Code promo (optionnel)"
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />
        <Button
          label={loading ? "Simulation..." : "Lancer la simulation"}
          intent="primary"
          size="sm"
          onClick={handleSimulate}
          disabled={loading}
        />
        {error && <p className="text-sm text-danger">{error}</p>}
        {result && (
          <div className="rounded-md border border-primary/20 bg-primary/5 p-4 space-y-2">
            <div className="text-sm font-medium text-text">Résultat</div>
            <div className="text-sm space-y-1 text-muted">
              <div>Prix de base: {result.base_price.toFixed(2)} CAD</div>
              <div>
                Promotions:{" "}
                {result.promotions_applied.length
                  ? result.promotions_applied
                      .map((p) => `${p.name} (-${p.discount_amount.toFixed(2)} CAD)`)
                      .join(", ")
                  : "—"}
              </div>
              <div>Rabais: {result.discount_amount.toFixed(2)} CAD</div>
              <div className="font-medium text-text">Prix final: {result.final_price.toFixed(2)} CAD</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
